import { Stack } from '@mui/material'
import { isAxiosError } from 'axios'
import React, { useEffect, useState } from 'react'

import { LoadingButton } from 'components/common/LoadingButton'
import { ResponseAlertNotification } from 'components/status-checks/common/ResponseAlertNotification'
import { ResponseBodyField } from 'components/status-checks/common/ResponseBodyField'
import { ResponseCodeField } from 'components/status-checks/common/ResponseCodeField'
import { useValidateEndpointConfiguration } from 'components/status-checks/hooks/use-validate-endpoint-configuration'
import { type StatusCheckDetailsContextValues } from 'context/status-checks/StatusCheckDetailsContext'
import { type MultiValueTags } from 'types/tags'

import type {
  EndpointConfiguration,
  TeamExternalIntegration
} from 'types/api/status-checks/status-check'

type AlertSeverity = 'error' | 'success' | null

const TestTeamExternalIntegration: React.FC<{
  authenticate: boolean
  buttonText?: string
  disabled?: boolean
  endpointConfiguration?: EndpointConfiguration
  integration: TeamExternalIntegration
  statusCheckId?: string
  statusCheckMultiSelectTags?: MultiValueTags | undefined
  onError(error: unknown): void
  onSuccess: StatusCheckDetailsContextValues['handleSuccess']
}> = ({
  authenticate,
  buttonText,
  disabled,
  endpointConfiguration,
  integration,
  statusCheckId,
  statusCheckMultiSelectTags,
  onError,
  onSuccess
}) => {
  const [isCanceling, setIsCanceling] = useState(false)
  const [responseBody, setResponseBody] = useState<string | null>(null)
  const [responseCode, setResponseCode] = useState<number | null>(null)
  const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>(null)
  const [abortController, setAbortController] = useState(new AbortController())
  const { aborted: isAborted } = abortController.signal

  const {
    isLoading,
    reset: resetEndpointConfiguration,
    validateEndpointConfiguration
  } = useValidateEndpointConfiguration(abortController)

  const shouldDisplayResponseDetail =
    !isLoading && (alertSeverity === 'error' || !authenticate)

  const handleTestClick = async () => {
    resetEndpointConfiguration()

    try {
      const { testEndpointResponse } = await validateEndpointConfiguration(
        integration,
        {
          authenticate,
          endpointConfiguration,
          statusCheckId,
          statusCheckMultiSelectTags
        }
      )

      // Update local state
      setAlertSeverity('success')
      setResponseBody(testEndpointResponse.responseBody ?? null)
      setResponseCode(testEndpointResponse.httpStatusCode ?? null)

      // Notify parent component of response
      onSuccess(testEndpointResponse)
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        setAlertSeverity('error')

        const { data, status } = error.response
        const { httpStatusCode, responseBody } = data ?? {}

        // Certain request errors will simply return a string response.
        if (typeof data === 'string') {
          setResponseBody(data)
          setResponseCode(status)
        } else {
          setResponseBody(responseBody)
          setResponseCode(httpStatusCode)
        }
      }

      onError(error)
    } finally {
      setIsCanceling(false)
    }
  }

  const handleAbortRequest = async () => {
    abortController.abort()

    setAlertSeverity(null)
    setIsCanceling(true)
    setResponseBody('Canceled')
    resetEndpointConfiguration()
  }

  // Resets local state and clears endpoint configuration if the integration or endpoint configuration changes.
  useEffect(() => {
    setAlertSeverity(null)
    setResponseBody(null)
    setResponseCode(null)

    resetEndpointConfiguration()
  }, [integration?.name, endpointConfiguration, resetEndpointConfiguration])

  // Reinitializes the abort controller if the previous request was aborted.
  useEffect(() => {
    setAbortController((controller) =>
      isAborted ? new AbortController() : controller
    )
  }, [isAborted])

  return (
    <Stack data-test-test-status-check-integration spacing={2}>
      <Stack direction="row" spacing={2}>
        <LoadingButton
          color="primary"
          data-testid={
            authenticate
              ? 'team-integration-auth-test-auth'
              : 'team-integration-auth-test-request'
          }
          isDisabled={!integration.url || isLoading || isCanceling || disabled}
          isLoading={isLoading || isCanceling}
          size="small"
          variant="contained"
          onClick={handleTestClick}
        >
          {buttonText ?? `Test ${authenticate ? 'Authentication' : 'Request'}`}
        </LoadingButton>
        {!authenticate && (
          <LoadingButton
            color="secondary"
            data-testid="cancel-test-status-check"
            isDisabled={!isLoading || isCanceling}
            isLoading={isCanceling}
            size="small"
            sx={{ ml: 1 }}
            variant="contained"
            onClick={handleAbortRequest}
          >
            {isCanceling ? 'Canceling Request...' : 'Cancel Request'}
          </LoadingButton>
        )}
      </Stack>
      {!isLoading && !isCanceling && (
        <>
          <ResponseAlertNotification alertSeverity={alertSeverity}>
            {alertSeverity === 'error'
              ? 'Monitor URL returned an error or was unreachable. See response details below.'
              : 'Authenticated'}
          </ResponseAlertNotification>
          {shouldDisplayResponseDetail && (
            <>
              <ResponseCodeField httpStatusCode={responseCode} />
              <ResponseBodyField responseBody={responseBody} />
            </>
          )}
        </>
      )}
    </Stack>
  )
}

export default TestTeamExternalIntegration
